import { sqliteTable, text, real, integer, index, uniqueIndex } from "drizzle-orm/sqlite-core";
import { sql } from "drizzle-orm";
import { accounts } from "./accounts";

export const GL_SOURCE_TYPES = [
  "invoice",
  "invoice_cogs",
  "payment",
  "payment_allocation",
  "payment_adjustment",
  "journal_entry",
  "wht_certificate",
  "tax_point_transition",
  "inventory_valuation",
  "bank_reconciliation",
  "cheque_deposit",
  "cheque_clearing",
  "cheque_bounce",
] as const;

export type GlSourceType = (typeof GL_SOURCE_TYPES)[number];

// ============================================================================
// gl_entries — General Ledger (auto-generated from invoice/payment/JE)
// ============================================================================
export const glEntries = sqliteTable("accounting_gl_entries", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  branchId: text("branch_id"),
  accountId: text("account_id").notNull().references(() => accounts.id), // FK → accounts
  postingDate: text("posting_date").notNull(),
  debit: real("debit").notNull().default(0),
  credit: real("credit").notNull().default(0),
  balance: real("balance").notNull().default(0), // running balance
  // Multi-currency context (#294). debit/credit are in base currency (THB);
  // currencyCode + exchangeRate document the source-document rate so reports
  // can later split per-currency or flag FX exposure. THB-only flows use
  // the defaults (THB, 1) transparently.
  currencyCode: text("currency_code").notNull().default("THB"),
  exchangeRate: real("exchange_rate").notNull().default(1),
  partyType: text("party_type"),
  partyId: text("party_id"),
  sourceType: text("source_type", {
    // Epic #443 — added "invoice_cogs" so COGS journal entries posted by
    // `accounting/services/public-api/post-invoice-cogs.ts` can sit on
    // gl_entries alongside the regular AR/Revenue lines without conflating
    // idempotency / reverse logic. Distinct sourceType means
    // `reverseGlEntries('invoice', invoiceId)` only touches the AR leg, and
    // `reverseGlEntries('invoice_cogs', invoiceId)` only touches the COGS
    // leg — each helper stays focused.
    //
    // Storage is `text` with a TS-only enum guard, so adding a value is a
    // pure code change (no DB migration required). Existing prod gl_entries
    // rows can never violate this since they were inserted under the old
    // enum subset.
    // Issue #503 — added "inventory_valuation" for the inventory movement
    // valuation bridge. This remains a TS-only guard over a TEXT column, so
    // no DB migration is required.
    enum: GL_SOURCE_TYPES,
  }).notNull(),
  sourceId: text("source_id").notNull(),
  isReversal: integer("is_reversal").notNull().default(0), // 0 = original, 1 = reversal entry
  reversalOf: text("reversal_of"), // ID of the original GL entry being reversed
  // Epic #446 — sequential row index (0, 1, 2, ...) within the batch
  // produced by a single createGlEntries / reverseGlEntries call. Combined
  // with a partial UNIQUE index on (companyId, sourceType, sourceId,
  // lineNo) WHERE isReversal=0, this defends against duplicate posting at
  // the DB layer: a re-fired hook trying to insert lineNo=0 for the same
  // invoice will hit the UNIQUE constraint instead of silently double-
  // posting. The app-level idempotency probe in postInvoiceGl is the
  // first line of defence; this is the second.
  //
  // Backfilled to row_number()-equivalent for legacy rows in migration
  // 0029 so existing posts cluster cleanly under one lineNo sequence per
  // (sourceType, sourceId, isReversal) group.
  lineNo: integer("line_no").notNull().default(0),
  costCenterId: text("cost_center_id"), // FK → cost_centers (nullable)
  // Polymorphic line-level reference (EPIC #412 Phase 1) — denormalised from
  // the source document line (invoice_item or journal_entry_item) so that
  // analytic reports can group ledger entries by vehicle/asset/rental/etc.
  // without joining back through source tables.
  lineReferenceModule: text("line_reference_module"),
  lineReferenceDoctype: text("line_reference_doctype"),
  lineReferenceId: text("line_reference_id"),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  companyAccountIdx: index("gl_company_account_idx").on(table.companyId, table.accountId, table.createdAt),
  companyPostingDateIdx: index("gl_company_posting_date_idx").on(table.companyId, table.postingDate),
  sourceIdx: index("gl_source_idx").on(table.sourceType, table.sourceId),
  accountPostingIdx: index("gl_account_posting_idx").on(table.accountId, table.postingDate, table.createdAt),
  glLineRefIdx: index("gl_line_ref_idx").on(table.companyId, table.lineReferenceModule, table.lineReferenceId),
  // Epic #446 — partial unique index on the posting key. Two original posts
  // for the same (companyId, sourceType, sourceId) cannot both succeed:
  // either app-level idempotency in postInvoiceGl skips the second post, or
  // the DB rejects it. Reversals are exempt (isReversal=1) so a posted-then-
  // reversed-then-reposted invoice is allowed.
  glUniquePostIdx: uniqueIndex("gl_unique_post_idx")
    .on(table.companyId, table.sourceType, table.sourceId, table.lineNo)
    .where(sql`${table.isReversal} = 0`),
}));

// Type exports
export type GlEntry = typeof glEntries.$inferSelect;
export type NewGlEntry = typeof glEntries.$inferInsert;
